(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? factory() :
    typeof define === 'function' && define.amd ? define('lazyloader', factory) :
    (factory());
}(this, (function () { 'use strict';

    /**
     * @param {string} prop
     * @param {string} value
     * @returns {boolean}
     */

    /**
     * @param {(string|number)} num
     * @returns {number}
     */
    const roundDecimals = (num, decimals = 4) => parseFloat(num.toFixed(decimals));

    const s4 = () => Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    /**
     * @returns {string}
     */
    const uniqueID = () => s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();

    /**
     * @param {String|number} needle
     * @param {Array} heystack
     * @returns {boolean}
     */
    const isInArray = (needle, heystack) => heystack.indexOf(needle) > -1;

    // TODO: cache?
    /**
     *
     * @param {HTMLElement} element
     * @param {string} property
     * @returns {string}
     */
    const getComputed = (element, property) => window.getComputedStyle(element).getPropertyValue(property);

    const supportedExtensions = {
        image: ['jp[e]?g', 'jpe', 'jif', 'jfif', 'jfi', 'gif', 'png', 'tif[f]?', 'bmp', 'dib', 'webp', 'ico', 'cur', 'svg'],
        audio: ['mp3', 'ogg', 'oga', 'spx', 'ogg', 'wav'],
        video: ['mp4', 'ogg', 'ogv', 'webm']
    };
    const supportedTags = {
        image: ['img', 'picture'],
        audio: ['audio'],
        video: ['video'],
        other: ['iframe'] // TODO: ...
    };
    const allSupportedTags = [...new Set(Object.values(supportedTags).reduce((a, b) => a.concat(b)))];

    const base64head = ';base64,';

    /**
     * @param {Object} item
     * @returns {Object}
     */
    class Resource {
        constructor(item) {
            const isElement = item.element instanceof HTMLElement;

            this.url = item.url;
            this.consistent = isElement && isInArray(item.element.tagName.toLowerCase(), allSupportedTags);
            this.element = isElement ? item.element : null;
            this.tagName = this.consistent ? this.element.tagName.toLowerCase() : null;
            this.type = null;
            this.extension = null;

            if (!this.url) {
                return;
            }

            this.url = item.url;

            // TODO: check if really needed
            // cleanup es: url.jpg, url.jpg x2 ...
            if (!new RegExp(`${base64head}`).test(this.url)) {
                this.url = this.url
                    .split(',')
                    .pop() // last
                    .split(' ')
                    .reduce((x, y) => (x.length > y.length ? x : y)); // longest in order to skip "x2", "" etc...
            }

            for (let format in supportedExtensions) {
                const extensions = supportedExtensions[format].join('|');

                if (new RegExp(`(.(${extensions})$)|data:${format}/(${extensions})${base64head}`).test(this.url)) {
                    const matches = this.url.match(new RegExp(`.(${extensions})$`, 'g')) || this.url.match(new RegExp(`^data:${format}/(${extensions})`, 'g'));

                    if (null !== matches) {
                        this.type = format;
                        this.extension = matches[0].replace(`data:${format}/`, '').replace('.', '');
                        this.tagName = this.tagName ? this.tagName : supportedTags[format][0];

                        break;
                    }
                }
            }

            if (this.consistent && (this.tagName === 'audio' || this.tagName === 'video' || this.tagName === 'iframe')) {
                this.type = this.tagName;
            } else if (this.tagName) {
                for (let format in supportedTags) {
                    if (isInArray(this.tagName, supportedTags[format])) {
                        this.type = format;
                        break;
                    }
                }
            }
        }

        static isResource(item) {
            return typeof item === 'object' && 'tagName' in item && 'type' in item && 'url' in item && 'extension' in item && 'element' in item;
        }
    }

    /**
     *
     */
    class LoaderPromise extends Promise {
        constructor(fn) {
            super((resolve, reject) => {
                fn(resolve, reject, value => {
                    try {
                        return this._progress.forEach(listener => listener(value));
                    } catch (error) {
                        reject(error);
                    }
                });
            });

            this._progress = [];
        }

        progress(handler) {
            if (typeof handler === 'function') {
                this._progress.push(handler);
            }

            return this;
        }
    }

    /**
     *
     * @param {HTMLElement} [element=document.body]
     * @param {Object} [options={ srcAttr: 'src', srcsetAttr: 'srcset', backgrounds: false }]
     * @returns {Array}
     */
    const find = (element = document.body, options = {}) => {
        let settings = {
            ...{
                srcAttributes: {
                    src: !!!options.lazy ? 'src' : 'data-src',
                    srcset: !!!options.lazy ? 'srcset' : 'data-srcset'
                },
                sizesAttributes: {
                    sizes: !!!options.lazy ? 'sizes' : 'data-sizes',
                    media: !!!options.lazy ? 'media' : 'data-media'
                },
                backgrounds: false
            },
            ...options
        };

        let collection = [];

        const tagsSelector = allSupportedTags.join(',');
        const srcAttributesValues = Object.values(settings.srcAttributes);
        const srcAttributesSelector = srcAttributesValues.map(x => `[${x}]`).join(',');

        let targets = [...element.querySelectorAll(tagsSelector)].filter(el => !el.parentElement || el.parentElement.tagName.toLowerCase() !== 'picture');
        if (element.matches(tagsSelector) && (!element.parentElement || element.parentElement.tagName.toLowerCase() !== 'picture')) {
            targets.push(element);
        }

        targets.forEach(target => {
            let source = target;

            if (target.querySelectorAll('source').length) {
                source = target.querySelectorAll('source');
                source = [...source][0];
            }

            if (source.matches(srcAttributesSelector)) {
                let attribute = '';

                for (let i = 0, j = srcAttributesValues.length; i < j; i++) {
                    attribute = source.getAttribute(srcAttributesValues[i]);
                    if (attribute) {
                        break;
                    }
                }

                collection.push(
                    new Resource({
                        element: target,
                        url: attribute
                    })
                );
            }
        });

        if (true === settings.backgrounds) {
            targets = [...element.querySelectorAll('*')];
            targets.push(element);
            targets = targets.filter(target => !target.matches(tagsSelector));
            targets = targets.filter(target => getComputed(target, 'background-image') !== 'none');
            targets.forEach(target => {
                const url = getComputed(target, 'background-image').match(/\((.*?)\)/);

                if (null !== url && url.length >= 2) {
                    collection.push(
                        new Resource({
                            element: target,
                            url: url[1].replace(/('|")/g, '')
                        })
                    );
                }
            });
        }

        return collection;
    };

    /**
     *
     * @param {HTMLElement} el
     * @param {Object} attrs
     * @returns {void}
     */
    const switchAttributes = (el, attrs) => {
        Object.keys(attrs).forEach(attr => {
            let dataAttr = attrs[attr];
            dataAttr = dataAttr === 'src' || dataAttr === 'srcset' ? `data-${dataAttr}` : dataAttr; // TODO: refactory
            const dataAttrVal = el.getAttribute(dataAttr);

            if (dataAttrVal) {
                el.setAttribute(attr, dataAttrVal);
                el.removeAttribute(dataAttr);
            }
        });
    };

    /**
     *
     * @param {HTMLElement} el
     * @param {HTMLElement} attributes
     * @returns {void}
     */
    const copyAttributes = (el, target, attributes) =>
        attributes.forEach(attr => {
            const attribute = target.getAttribute(attr);
            if (attribute) {
                el.setAttribute(attr === 'src' || attr === 'srcset' ? `data-${attr}` : attr, attribute); // TODO: refactory
            }
        });

    /**
     *
     * @param {HTMLElement} el
     * @param {Array} attributes
     * @returns {void}
     */
    const removeAttributes = (el, attributes) => attributes.forEach(attr => el.removeAttribute(attr));

    /**
     *
     * @param {Number} percentage
     * @returns {Number}
     */
    const roundPercentage = percentage => {
        if (percentage > 100) {
            percentage = 100;
        } else if (percentage < 0) {
            percentage = 0;
        } else {
            percentage = roundDecimals(percentage, 4);
        }

        return percentage;
    };

    const isIntersectionObserverSupported = 'IntersectionObserver' in window;

    /**
     *
     */
    class Viewport {
        constructor() {
            this._window = window;
            this._html = document.documentElement;
            this._body = document.body;

            this._frame1 = this._window;
            this._frame2 = this._html || this._body;
            this._prefix1 = 'inner';
            this._prefix2 = 'offset';
            if (!this._prefix1 + 'Width' in this._frame1) {
                this._prefix1 = 'client';
                this._frame1 = this._frame2;
            }

            this._offsetWidth = 0;
            this._offsetHeight = 0;
            this._width = 0;
            this._height = 0;
            this._scrollTop = 0;
            this._scrollLeft = 0;

            this._lock = false;

            this.calcAll();
        }

        get offsetWidth() {
            return this._offsetWidth;
        }
        get offsetHeight() {
            return this._offsetHeight;
        }
        get width() {
            return this._width;
        }
        get height() {
            return this._height;
        }
        get scrollTop() {
            return this._scrollTop;
        }
        get scrollLeft() {
            return this._scrollLeft;
        }
        get all() {
            return {
                offsetWidth: this.offsetWidth,
                offsetHeight: this.offsetHeight,
                width: this.width,
                height: this.height,
                scrollTop: this.scrollTop,
                scrollLeft: this.scrollLeft
            };
        }

        calcOffsetWidth() {
            this._offsetWidth = this._body[this._prefix2 + 'Width'];
            return this.offsetWidth;
        }
        calcOffsetHeight() {
            this._offsetHeight = this._body[this._prefix2 + 'Height'];
            return this.offsetHeight;
        }
        calcWidth() {
            this._width = this._frame1[this._prefix1 + 'Width'];
            return this.width;
        }
        calcHeight() {
            this._height = this._frame1[this._prefix1 + 'Height'];
            return this.height;
        }
        calcScrollTop() {
            this._scrollTop = this._frame2.scrollTop || 0;
            return this.scrollTop;
        }
        calcScrollLeft() {
            this._scrollLeft = this._frame2.scrollLeft || 0;
            return this.scrollLeft;
        }
        calcAll() {
            this.calcOffsetWidth();
            this.calcOffsetHeight();
            this.calcWidth();
            this.calcHeight();
            this.calcScrollTop();
            this.calcScrollLeft();
            return this.all;
        }

        get lock() {
            return this._lock;
        }

        set lock(bool) {
            this._lock = bool;

            if (this._lock) {
                this._body.style.top = -this.calcScrollTop() + 'px';
                this._body.style.left = -this.calcScrollLeft() + 'px';

                let scrollBarWidth = this.calcOffsetWidth();
                let scrollBarHeight = this.calcOffsetHeight();

                this._html.style.top = '0px';
                this._html.style.left = '0px';
                this._html.style.position = 'fixed';
                this._body.style.position = 'fixed';
                this._html.style.width = '100%';
                this._body.style.width = '100%';

                let offsetWidth = this.calcOffsetWidth();
                let offsetHeight = this.calcOffsetHeight();
                scrollBarWidth = offsetWidth - scrollBarWidth;
                scrollBarHeight = offsetHeight - scrollBarHeight;

                this._body.style.width = offsetWidth - scrollBarWidth + 'px';
                this._body.style.height = offsetHeight - scrollBarHeight + 'px';
            } else {
                let scrollTop = Math.abs(parseFloat(this._body.style.top));
                let scrollLeft = Math.abs(parseFloat(this._body.style.left));

                this._html.style.position = '';
                this._html.style.top = '';
                this._html.style.left = '';
                this._html.style.width = '';
                this._body.style.position = '';
                this._body.style.top = '';
                this._body.style.left = '';
                this._body.style.width = '';

                this._window.scroll({
                    top: scrollTop,
                    left: scrollLeft,
                    behavior: 'instant'
                });
            }
        }
    }

    /**
     * @param {HTMLElement} element
     * @returns {Boolean}
     */
    const isElementInViewport = element => !!getBoundsViewportIntersection(element.getBoundingClientRect()).ratio;

    /**
     * @param {DOMRect} bounds
     * @returns {Object}
     */
    const getBoundsViewportIntersection = bounds => {
        const viewport = new Viewport();

        if (bounds.right < 0 || bounds.bottom < 0 || bounds.left > viewport.width || bounds.top > viewport.height) {
            return {
                x: 0,
                y: 0,
                ratio: 0
            };
        }

        const offTop = bounds.top < 0;
        const offBottom = bounds.top + bounds.height > viewport.height;
        const offLeft = bounds.left < 0;
        const offRight = bounds.left + bounds.width > viewport.width;

        if (!offTop && !offBottom && !offLeft && !offRight) {
            return {
                x: 100,
                y: 100,
                ratio: 100
            };
        }

        let offY = 0;
        let offX = 0;

        if (offTop) {
            offY = Math.abs(bounds.top);
        }

        if (offBottom) {
            offY = bounds.top + bounds.height - viewport.height;
        }

        if (offLeft) {
            offX = Math.abs(bounds.left);
        }

        if (offRight) {
            offX = bounds.left + bounds.width - viewport.width;
        }

        const percentageX = roundPercentage(((bounds.width - offX) * 100) / (bounds.width || 1));
        const percentageY = roundPercentage(((bounds.height - offY) * 100) / (bounds.height || 1));

        return {
            x: percentageX,
            y: percentageY,
            ratio: Math.min(percentageX, percentageY)
        };
    };

    const userAgent = navigator.userAgent.toLowerCase();

    const vendorsPrefixes = ['WebKit', 'Moz', 'O', 'Ms', ''];

    const MutationObserver = (() => {
        for (let i = 0; i < vendorsPrefixes.length; i++) {
            if (vendorsPrefixes[i] + 'MutationObserver' in window) {
                return window[vendorsPrefixes[i] + 'MutationObserver'];
            }
        }

        return false;
    })();

    const transitionEndEventName = (() => {
        const el = document.createElement('div');

        const transEndEventNames = {
            WebkitTransition: 'webkitTransitionEnd',
            MozTransition: 'transitionend',
            OTransition: 'oTransitionEnd otransitionend',
            msTransition: 'MSTransitionEnd',
            transition: 'transitionend'
        };

        for (let name in transEndEventNames) {
            if (el.style[name] !== undefined) {
                return transEndEventNames[name];
            }
        }

        return false;
    })();

    const intersectionObserverThreshold$1 = (segments = 1) => {
        let array = [];
        for (let i = 0; i < segments; i++) {
            array.push((2 * i) / 100);
        }
        return array;
    };

    const ID = uniqueID();
    const threshold = intersectionObserverThreshold$1(50);

    /**
     *
     * TODO: find a way to get a recap of what Resource items were loaded and what failed in collection list...
     */
    class Loader {
        constructor(options = {}) {
            this._options = {
                ...{
                    srcAttributes: {
                        src: !!!options.lazy ? 'src' : 'data-src',
                        srcset: !!!options.lazy ? 'srcset' : 'data-srcset'
                    },
                    sizesAttributes: {
                        sizes: !!!options.lazy ? 'sizes' : 'data-sizes',
                        media: !!!options.lazy ? 'media' : 'data-media'
                    },
                    lazy: false,
                    playthrough: false,
                    backgrounds: true,
                    sequential: false
                },
                ...options
            };

            this._collection = []; // resources (array of type Resource)
            this._queue = new Map(); // loading resources

            this._load = null; // main load Promise

            this._percentage = 0; // loading percentage
            this._state = 0; // 0: inactive - 1: busy

            if (!isIntersectionObserverSupported) {
                this.manuallyObservedElements = [];

                const manuallyObserve = () =>
                    this.manuallyObservedElements.filter(item => !item.intersected).forEach(item => {
                        if (!item.intersected && isElementInViewport(item.element)) {
                            item.intersected = true;
                            item.element.dispatchEvent(new CustomEvent(`intersected__${ID}`));
                        }
                    });

                window.addEventListener('scroll', manuallyObserve);
                window.addEventListener('resize', manuallyObserve);
                window.addEventListener('load', manuallyObserve);
            }
        }

        /**
         * @returns {Number} percentage
         */
        get percentage() {
            return this._percentage;
        }

        /**
         * @param {(Array|HTMLElement)} collection
         */
        set collection(collection) {
            if (this._state === 0) {
                if (collection instanceof NodeList) {
                    collection = [...collection];
                }

                if (collection instanceof HTMLElement) {
                    collection = find(collection, this._options);
                }

                if (typeof collection === 'string') {
                    collection = [new Resource({ url: collection })];
                }

                if (Resource.isResource(collection)) {
                    collection = [collection];
                }

                if (Array.isArray(collection)) {
                    this._collection = [];

                    collection.forEach(item => {
                        const pushCheck = resource => {
                            if (resource.type === 'image' || resource.type === 'video' || resource.type === 'audio' || (resource.type === 'iframe' && resource.consistent)) {
                                this._collection.push(resource);

                                return;
                            }

                            console.warn("Couldn't add resource to collection", resource);
                        };

                        if (item instanceof HTMLElement) {
                            find(item, this._options).forEach(resource => pushCheck(new Resource(resource)));
                        }

                        if (typeof item === 'string') {
                            pushCheck(new Resource({ url: item }));
                        }

                        if (Resource.isResource(item)) {
                            pushCheck(item);
                        }
                    });
                }
            }
        }

        /**
         *
         * @returns {Array} collection
         */
        get collection() {
            return this._collection;
        }

        /**
         *
         * @returns {void}
         */
        abort() {
            if (this._state === 0) {
                return;
            }

            this._state = 0;

            this._queue.forEach((data, element) => element.dispatchEvent(new CustomEvent(`abort__${ID}`)));
        }

        /**
         *
         * @returns {Promise}
         */
        load() {
            this._load = new LoaderPromise((resolve, reject, progress) => {
                if (this._state === 1) {
                    reject('This Loader instance is already in progress');
                }

                if (!this._collection.length) {
                    reject('Resources collection is empty');
                }

                this._state = 1;

                if (this._options.sequential) {
                    let loaded = 0;
                    const pipeline = () => {
                        if (this._state === 0) {
                            reject('Loader instance aborted');

                            this._collection = [];

                            return;
                        }

                        this.fetch(this._collection[loaded])
                            .catch(payload => {
                                console.warn(`${payload.event.detail.message}: ${payload.resource.url}`);

                                return payload;
                            })
                            .then(payload => {
                                if (this._state !== 0) {
                                    loaded++;
                                }

                                this._percentage = (loaded / this._collection.length) * 100;

                                // TODO: a more robust way to skip progress call in a then from a catch, possibly inside LoaderPromise?
                                if (this._state !== 0) {
                                    progress(payload);
                                }

                                if (loaded < this._collection.length) {
                                    pipeline();

                                    return;
                                }

                                this._state = 0;

                                resolve(payload);
                            });
                    };

                    pipeline();
                } else {
                    let loaded = 0;

                    for (let i = 0; i < this._collection.length; i++) {
                        if (this._state === 0) {
                            reject('Loader instance aborted');

                            this._collection = [];

                            break;
                        }

                        this.fetch(this._collection[i])
                            .catch(payload => {
                                console.warn(`${payload.event.detail.message}: ${payload.resource.url}`);

                                return payload;
                            })
                            .then(payload => {
                                if (this._state !== 0) {
                                    loaded++;
                                }

                                this._percentage = (loaded / this._collection.length) * 100;

                                // TODO: a more robust way to skip progress call in a then from a catch, possibly inside LoaderPromise?
                                if (this._state !== 0) {
                                    progress(payload);
                                }

                                if (loaded >= this._collection.length) {
                                    this._state = 0;

                                    resolve();
                                }
                            });
                    }
                }
            });

            return this._load;
        }

        /**
         *
         * @param {Resource} resource
         * @returns {Promise}
         */
        fetch(resource) {
            return new Promise((resolve, reject) => {
                const isConsistent = resource.consistent && document.body.contains(resource.element);
                const hasSources = isConsistent && resource.element.querySelectorAll('source').length;
                const createdElement = document.createElement(resource.tagName);

                let mainEventsTarget = createdElement;

                if (resource.type === 'iframe') {
                    createdElement.style.visibility = 'hidden';
                    createdElement.style.position = 'fixed';
                    createdElement.style.top = '-999px';
                    createdElement.style.left = '-999px';
                    createdElement.style.width = '1px';
                    createdElement.style.height = '1px';
                    document.body.appendChild(createdElement);
                }

                if (isConsistent) {
                    copyAttributes(createdElement, resource.element, Object.values(this._options.srcAttributes));
                    copyAttributes(createdElement, resource.element, Object.values(this._options.sizesAttributes));

                    if (hasSources) {
                        [...resource.element.querySelectorAll('source')].forEach(source => {
                            const createdSource = document.createElement('source');
                            copyAttributes(createdSource, source, Object.values(this._options.srcAttributes));
                            copyAttributes(createdSource, source, Object.values(this._options.sizesAttributes));
                            createdElement.append(createdSource);
                        });
                    }

                    if (resource.tagName === 'picture') {
                        // picture elements event listeners need to be attached to inner img elements
                        const img = resource.element.querySelector('img');
                        const createdImg = document.createElement('img');
                        copyAttributes(createdImg, img, Object.values(this._options.srcAttributes));
                        copyAttributes(createdImg, img, Object.values(this._options.sizesAttributes));
                        createdElement.append(createdImg);
                        mainEventsTarget = createdImg;
                    }
                }

                const finishPromise = (event, resolver) => {
                    if (isConsistent) {
                        switchAttributes(resource.element, this._options.srcAttributes);
                        switchAttributes(resource.element, this._options.sizesAttributes);

                        if (hasSources) {
                            [...resource.element.querySelectorAll('source')].forEach(source => {
                                switchAttributes(source, this._options.srcAttributes);
                                switchAttributes(source, this._options.sizesAttributes);
                            });
                        }

                        if (resource.type === 'video' || resource.type === 'audio') {
                            resource.element.load();
                        }

                        if (resource.type === 'iframe') {
                            createdElement.parentElement.removeChild(createdElement);
                        }
                    }

                    this._queue.delete(createdElement);

                    resolver({
                        event: event,
                        resource: resource
                    });
                };

                const prepareLoad = () => {
                    if (isConsistent) {
                        switchAttributes(createdElement, this._options.srcAttributes);
                        switchAttributes(createdElement, this._options.sizesAttributes);

                        if (hasSources) {
                            [...createdElement.querySelectorAll('source')].forEach(source => {
                                switchAttributes(source, this._options.srcAttributes);
                                switchAttributes(source, this._options.sizesAttributes);
                            });
                        }
                    } else {
                        createdElement.setAttribute('src', resource.url);
                    }

                    if (resource.type === 'video' || resource.type === 'audio') {
                        createdElement.load();
                    }
                };

                const dispatchEvent = (eventName = '', data = {}) => {
                    const event = new CustomEvent(eventName, {
                        detail: {
                            ...data,
                            ...{
                                resource: resource
                            }
                        }
                    });

                    if (resource.element) {
                        resource.element.dispatchEvent(event);
                    }

                    document.dispatchEvent(event);

                    return event;
                };

                let queuer = {
                    resource: resource,
                    observer: null,
                    element: createdElement
                };

                createdElement.addEventListener(`abort__${ID}`, () => {
                    removeAttributes(createdElement, Object.keys(this._options.srcAttributes));
                    removeAttributes(createdElement, Object.values(this._options.srcAttributes));
                    removeAttributes(createdElement, Object.keys(this._options.sizesAttributes));
                    removeAttributes(createdElement, Object.values(this._options.sizesAttributes));

                    if (hasSources) {
                        [...createdElement.querySelectorAll('source')].forEach(source => {
                            removeAttributes(source, Object.keys(this._options.srcAttributes));
                            removeAttributes(source, Object.values(this._options.srcAttributes));
                            removeAttributes(source, Object.keys(this._options.sizesAttributes));
                            removeAttributes(source, Object.values(this._options.sizesAttributes));
                        });
                    }

                    const dispatchedEvent = dispatchEvent('resourceError', { type: 'abortion', message: 'Resource load aborted' });

                    finishPromise(dispatchedEvent, reject);
                });

                mainEventsTarget.addEventListener('error', e => {
                    const dispatchedEvent = dispatchEvent('resourceError', {
                        type: e.type,
                        message: 'Resource failed to load' // TODO: a way to retrieve http error code ?
                    });

                    finishPromise(dispatchedEvent, reject);
                });

                if (resource.type === 'image' || resource.type === 'iframe') {
                    mainEventsTarget.addEventListener('load', e => {
                        if (e.target.tagName.toLowerCase() === 'iframe' && !e.target.getAttribute('src')) {
                            return; // iframes fire "load" event when ready in dom
                        }

                        const dispatchedEvent = dispatchEvent('resourceLoad', {
                            type: e.type
                        });

                        finishPromise(dispatchedEvent, resolve);
                    });
                }

                if (resource.type === 'audio' || resource.type === 'video') {
                    mainEventsTarget.addEventListener(this._options.playthrough ? 'canplaythrough' : 'loadedmetadata', e => {
                        const dispatchedEvent = dispatchEvent('resourceLoad', {
                            type: e.type
                        });

                        finishPromise(dispatchedEvent, resolve);
                    });
                }

                if (resource.element instanceof HTMLElement && this._options.lazy) {
                    if (isIntersectionObserverSupported) {
                        queuer.observer = new IntersectionObserver(
                            (entries, observer) => {
                                entries.forEach(entry => {
                                    if (entry.intersectionRatio > 0) {
                                        observer.unobserve(entry.target);

                                        prepareLoad();
                                    }
                                });
                            },
                            {
                                root: null,
                                rootMargin: '0px',
                                threshold: threshold
                            }
                        );

                        queuer.observer.observe(resource.element);
                    } else {
                        if (isElementInViewport(resource.element)) {
                            prepareLoad();
                        } else {
                            this.manuallyObservedElements.push({
                                element: resource.element,
                                intersected: false
                            });

                            resource.element.addEventListener(`intersected__${ID}`, () => prepareLoad());
                        }
                    }

                    this._queue.set(resource.element, queuer);
                } else {
                    this._queue.set(createdElement, queuer);

                    prepareLoad();
                }
            });
        }
    }

    const logEl = document.querySelector('#console');
    const log = (...message) => {
        console.log(...message);

        if (!logEl.hasChildNodes()) {
            const ol = document.createElement('ol');
            logEl.append(ol);
        }

        const list = logEl.querySelector('ol');
        const li = document.createElement('li');
        li.innerHTML = message;
        list.append(li);

        logEl.scrollTop = list.offsetHeight;
    };

    const pageLoader = new Loader({
        lazy: true,
        playthrough: true,
        backgrounds: true
    });

    pageLoader.collection = document.querySelectorAll('.playground');

    window.addEventListener(
        'load',
        () => {
            const pageLoad = pageLoader.load();

            pageLoad.progress(e => {
                if (!e.resource.element) {
                    return;
                }

                const container = e.resource.element.matches('.playground') ? e.resource.element : e.resource.element.closest('.playground');
                const percentage = container.querySelector('.playground__percentage');
                const item = e.resource.element.closest('.playground__item');

                if (percentage) {
                    percentage.classList.add('visible');
                }

                if (item) {
                    e.resource.element.closest('.playground__item').classList.add('loaded');
                }

                if (percentage) {
                    percentage.children[0].dataset.percentage =
                        (container.querySelectorAll('.playground__item.loaded').length / container.querySelectorAll('.playground__item').length) * 100;
                }

                log('Total page load: ' + pageLoader.percentage + '% ' + e.resource.url, e);
            });
            pageLoad.catch(error => console.log(error)).then(e => log('All done!'));
        },
        false
    );

    document.querySelectorAll('img').forEach(img => img.addEventListener('resourceError', e => e.detail.resource.element.classList.add('missing')));

})));
